import type { ReportTypeId } from './reportTypes';

export interface SectionDepthRequirement {
  sectionId: string;
  minChars?: number;
  minBullets?: number;
  minBulletChars?: number;
  optional?: boolean;
  severity?: 'warn' | 'block';
}

export const DEFAULT_PROSE_MIN = 200;
export const DEFAULT_EXEC_MIN = 600;

export const SECTION_DEPTH_REQUIREMENTS: Record<ReportTypeId, SectionDepthRequirement[]> = {
  feasibility: [
    { sectionId: 'executiveSummary', minChars: DEFAULT_EXEC_MIN },
    // minBulletChars 60 → 45 (2026-08-03): the 60 floor was calibrated
    // against the old 40-word constraint-stub boilerplate. Value-first
    // R-68 stubs ("Grid capacity — Constrained (mitigation pathway
    // required)") legitimately measure 53-57 chars; at 60 the gate would
    // hard-fail deliberate concision — the same false-positive class the
    // RCA removed. 45 still rejects fragments ("TBD", "See above").
    { sectionId: 'keyFindings', minBullets: 3, minBulletChars: 45 },
    { sectionId: 'conclusion', minChars: 400 },
  ],
  environmental: [
    { sectionId: 'executiveSummary', minChars: DEFAULT_EXEC_MIN },
    { sectionId: 'conclusion', minChars: 400 },
  ],
  'power-availability': [
    { sectionId: 'executiveSummary', minChars: DEFAULT_EXEC_MIN },
    // STAGE 5.2 commit 2 — gridCapacity is a feasibility section now.
    { sectionId: 'conclusion', minChars: 400 },
  ],
  'test-fit': [
    { sectionId: 'executiveSummary', minChars: DEFAULT_EXEC_MIN },
    // sitingNotes → parcelOverview (2026-08-04, propagation audit): the
    // test-fit generation NEVER produces `sitingNotes` — the typed path
    // populates it only for feasibility, and TEST_FIT_SCHEMA has no such
    // field — so the old requirement was structurally unpassable (the same
    // false-positive class as the feasibility keyFindings bullet counter;
    // undetonated only because no test-fit has run since the depth gate was
    // wired to the QA-1 hold). `parcelOverview` maps to
    // `testFitAnalysis.siteConstraints`, which the schema REQUIRES.
    { sectionId: 'parcelOverview', minChars: DEFAULT_PROSE_MIN },
  ],
  'supply-chain-risk': [
    { sectionId: 'executiveSummary', minChars: DEFAULT_EXEC_MIN },
  ],
  'custom-brief': [
    { sectionId: 'executiveSummary', minChars: DEFAULT_EXEC_MIN },
    { sectionId: 'conclusion', minChars: 400 },
  ],
  'public-sentiment': [
    { sectionId: 'executive', minChars: DEFAULT_EXEC_MIN },
    { sectionId: 'stakeholderMap', minChars: DEFAULT_PROSE_MIN },
    { sectionId: 'perBtmSentiment', minChars: DEFAULT_PROSE_MIN },
    { sectionId: 'themeDeepDives', minChars: DEFAULT_PROSE_MIN },
    { sectionId: 'trendAnalysis', minChars: DEFAULT_PROSE_MIN },
    { sectionId: 'comparableSiteBenchmark', minChars: DEFAULT_PROSE_MIN },
    { sectionId: 'mitigationPlaybook', minChars: DEFAULT_PROSE_MIN },
    { sectionId: 'appendix', minChars: 100 },
  ],
  'executive-summary': [
    { sectionId: 'executiveSummary', minChars: DEFAULT_EXEC_MIN },
    { sectionId: 'criticalRisks', minBullets: 3, minBulletChars: 60 },
    { sectionId: 'actionPlan', minBullets: 3, minBulletChars: 50 },
    { sectionId: 'conclusion', minChars: 400 },
  ],
  'water-resource': [
    { sectionId: 'executiveSummary', minChars: DEFAULT_EXEC_MIN },
  ],
  interconnection: [
    { sectionId: 'executiveSummary', minChars: DEFAULT_EXEC_MIN },
  ],
  /*
   * FIN-1 (wf w7nuyquwd), revised for audit 3.8 — Financial Viability Report.
   *
   * `[]` said "this type has no depth expectations at all". It does have one,
   * and exactly one that this gate can honestly measure.
   *
   * WHY ONLY `conclusion`. `checkSectionDepth` measures whatever
   * `serializeSectionContent` can render for a section id, and that resolver
   * knows a section only through `SECTION_FIELD_PATHS[reportType]` or
   * `SECTION_FIELD_CANDIDATES`. Neither carries ANY of this type's body ids
   * (`decisionBand`, `financialSummary`, `capexOpexBreakdown`,
   * `cashflowStream`, `monteCarloBands`, `riskAdjustedWacc`, `sensitivity`,
   * `taxAndCommercial`, `financialActionPlan`) — they render live from
   * `report.financialAnalysis`, which the gate never reads. A requirement on
   * any of them would serialize to zero chars and fail on EVERY report,
   * forever, no matter how good the report is. That is precisely the
   * structurally-unpassable defect the `test-fit` note above documents
   * (`sitingNotes`), and it must not be re-created here.
   *
   * `conclusion` is different: `applyFinancialAnalysisResultToReport` stamps
   * `recommendation` + `topRisks` + `keyConditions` + `nextActions` +
   * `btmDecisionSummary` deterministically, and `serializeConclusion` reads
   * all five. A banded verdict serializes to ~250-300 chars, so 200 is a floor
   * the engine's own output clears while still catching the real defect: a
   * report that reached publish with NO conclusion at all.
   *
   * WARN, not block. A WITHHELD verdict (`band === null`) deliberately leaves
   * `conclusion` alone rather than fabricating a recommendation enum value, so
   * a withheld report can legitimately serialize to nothing here. Blocking
   * would hold a report for being honest about not having a verdict.
   */
  financial: [
    { sectionId: 'conclusion', minChars: 200, severity: 'warn' },
  ],
};
